import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface AliasValidationResult {
  isValid: boolean;
  error: string | null;
  isChecking: boolean;
}

export const useAliasValidation = () => {
  const { t } = useTranslation();
  const [isChecking, setIsChecking] = useState(false);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateAliasFormat = useCallback((text: string): { isValid: boolean; error: string | null } => {
    if (text.length < 3) {
      return { isValid: false, error: t('alias.validation.too_short') || 'Alias must be at least 3 characters long' };
    }
    if (text.length > 50) {
      return { isValid: false, error: t('alias.validation.too_long') || 'Alias must be less than 50 characters' };
    }
    if (/[^a-zA-Z0-9-]/.test(text)) {
      return { isValid: false, error: t('alias.validation.special_chars') || 'Alias can only contain letters, numbers, and hyphens' };
    }
    return { isValid: true, error: null };
  }, [t]);

  const checkAliasAvailability = useCallback(async (
    alias: string, 
    checkEndpoint: (alias: string) => Promise<any>
  ): Promise<{ isAvailable: boolean; error?: string }> => {
    setIsChecking(true);
    try {
      await checkEndpoint(alias);
      return { isAvailable: true };
    } catch (error: any) {
      console.error('Alias availability check error:', error);
      
      if (error.response) {
        console.error('Error response:', error.response);
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      
      if (error.response?.status === 409) {
        return { isAvailable: false, error: t('alias.validation.already_taken') || 'This alias is already taken by another store' };
      }
      
      return { isAvailable: false, error: error.message || t('alias.validation.server_error') || 'Error checking alias availability' };
    } finally {
      setIsChecking(false);
    }
  }, [t]);

  const debouncedCheckAvailability = useCallback((
    alias: string,
    checkEndpoint: (alias: string) => Promise<any>,
    onResult: (result: { isAvailable: boolean; error?: string }) => void
  ) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setIsValidationInProgress(true);

    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await checkAliasAvailability(alias, checkEndpoint);
        
        setIsValidationInProgress(false);
        onResult(result);
      } catch (error: any) {
        console.error('Debounced validation error:', error);
        
        setIsValidationInProgress(false);
        const errorMessage = error?.response?.data?.message 
          || error?.message 
          || t('alias.validation.server_error') 
          || 'Unable to check if this link is available. Please try again.';
        onResult({ isAvailable: false, error: errorMessage });
      }
    }, 1000);
  }, [checkAliasAvailability, t]);

  const cancelPendingRequest = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsValidationInProgress(false);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    validateAliasFormat,
    checkAliasAvailability,
    debouncedCheckAvailability,
    cancelPendingRequest,
    isChecking,
    isValidationInProgress,
    currentAbortController: abortControllerRef.current
  };
};
